import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import {
  assignCustomer,
  CASE_GONE_SENTENCE,
  CASE_KEY,
  CASES_KEY,
  caseGone,
  ProblemError,
  setCaseContact,
  useCase,
  useCustomerProposal,
  type CandidateOut,
  type ContactOut,
  type CustomerOut,
  type CustomerProposalOut,
  type ProposalFieldsOut,
} from "@/api";
import { Bone } from "@/components/Bone";
import { Button } from "@/components/Button";
import { TextField } from "@/components/fields/TextField";
import { LockIcon } from "@/components/icons";
import { StateMessage } from "@/components/StateMessage";
import { otherPerson } from "@/lib/contactPerson";
import { gratingTypeLabel } from "@/lib/gratingTypes";
import { coatingValue, type MailRequest } from "@/lib/mailRequest";

import { contactFromMail, draftFromMail, SOURCE_WORDS, sourcesFromMail, type ContactDraft } from "./clues";
import { CustomerCard } from "./CustomerCard";
import { CustomerForm, type CustomerDraft } from "./CustomerForm";
import styles from "./CustomerFromMail.module.css";
import { CustomerSearch } from "./CustomerSearch";

type Mode = "card" | "choose" | "create" | "search";

/** The server's own rule, no stricter — the same sentence as the customer form's. */
const EMAIL_WITHOUT_AT = "Eine E-Mail-Adresse braucht ein „@“ — trag sie vollständig ein oder lass das Feld leer.";
const UNREADABLE = "Diese Mail konnte nicht gelesen werden — leg den Kunden von Hand an.";
const NOTHING_RECOGNISABLE = "In der Mail steht nichts, woran sich der Kunde erkennen lässt.";

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** The server's own sentence — or, where none came back, that it was not reached. */
function messageOf(cause: unknown): string {
  return cause instanceof ProblemError ? cause.userMessage : "Der Server ist nicht erreichbar. Versuch es gleich noch einmal.";
}

/** Why the proposal is not there, in the screen's words — 404 and 422 are answers, the rest a hiccup. */
function proposalTrouble(error: Error): { text: string; retry: boolean } {
  if (error instanceof ProblemError && error.status === 404) {
    return { text: "Diese Mail liegt nicht in diesem Vorgang — leg den Kunden von Hand an oder such ihn.", retry: false };
  }
  if (error instanceof ProblemError && error.status === 422) {
    return { text: "Dieser Eintrag ist keine Mail — leg den Kunden von Hand an oder such ihn.", retry: false };
  }
  return { text: "Die Mail lässt sich gerade nicht auswerten. Versuch es gleich noch einmal — oder leg den Kunden von Hand an.", retry: true };
}

/** „PLZ Ort", or nothing. */
function placeOf(customer: Pick<CustomerOut, "postalCode" | "city">): string | null {
  const place = [customer.postalCode, customer.city].filter((part) => part !== null && part.trim() !== "").join(" ");
  return place === "" ? null : place;
}

/** One line of what the mail gives, in the order a customer is read: who, how to reach, where. */
type ReadRow = { label: string; value: string; from: string };

/** The one read value that says nothing about who the customer is — what is asked for (spec 2026-10-23 R-6). */
const TYPE_ROW = "Rosttyp";
/** And the other (Task 15b): the coating the mails name. Said, never a field — it is not priced. */
const COATING_ROW = "Beschichtung";
/** What is asked for, not who asks. */
const ASKED_FOR: ReadonlySet<string> = new Set([TYPE_ROW, COATING_ROW]);

function readRows(fields: ProposalFieldsOut, request: MailRequest | null): ReadRow[] {
  const rows: ReadRow[] = [];
  const add = (label: string, clue: ProposalFieldsOut[keyof ProposalFieldsOut]) => {
    if (clue !== null) rows.push({ label, value: clue.value, from: SOURCE_WORDS[clue.source] });
  };
  add("Name", fields.name);
  add("Firma", fields.company);
  add("E-Mail", fields.email);
  // What the master is searched by beside the address (spec §4.2) — a firm's
  // own domain, never a freemailer's.
  add("Domain", fields.domain);
  add("Telefon", fields.phone);
  add("Mobil", fields.mobile);
  const address = [fields.street, fields.postalCode, fields.city].filter((clue) => clue !== null);
  if (address.length > 0) {
    const place = [fields.postalCode?.value, fields.city?.value].filter(Boolean).join(" ");
    rows.push({
      label: "Anschrift",
      value: [fields.street?.value, place].filter(Boolean).join(", "),
      from: [...new Set(address.map((clue) => SOURCE_WORDS[clue!.source]))].join(", "),
    });
  }
  add("Kundennummer", fields.customerNo);
  // Last, and in the catalogue's word, not the wire's („Barfußrost“, never
  // `barfuss`): what the mail asks for, beside who sent it. Taken at the
  // Eingang's own „Rosttyp“, never here.
  if (fields.gratingType !== null) {
    rows.push({
      label: TYPE_ROW,
      value: gratingTypeLabel(fields.gratingType.value),
      from: SOURCE_WORDS[fields.gratingType.source],
    });
  }
  // The coating the Vorgang's mails name (`CaseDetailOut.mailRequest`, Task
  // 13e/15b): RAL 7016 stood in the mail and nowhere here (UX-Zweitgutachten
  // Nr. 9). Read over the Vorgang's mails, so no single place to name — „aus
  // der Mail“. The planning says it is not priced; here it is only read.
  const coating = request !== null ? coatingValue(request) : null;
  if (coating !== null) rows.push({ label: COATING_ROW, value: coating, from: "aus der Mail" });
  return rows;
}

/** The contact as the screen edits it — blank where nothing is stored. */
function draftOf(contact: ContactOut): ContactDraft {
  return { name: contact.name ?? "", email: contact.email ?? "", phone: contact.phone ?? "" };
}

/** A contact with anything in it — a stored row of three blanks is none. */
function hasContact(draft: ContactDraft): boolean {
  return draft.name.trim() !== "" || draft.email.trim() !== "" || draft.phone.trim() !== "";
}

/** The same three values, as the server stores them (trimmed; an address in any case). */
function sameContact(a: ContactDraft, b: ContactDraft): boolean {
  return (
    a.name.trim() === b.name.trim() &&
    a.email.trim().toLowerCase() === b.email.trim().toLowerCase() &&
    a.phone.trim() === b.phone.trim()
  );
}

/** A choice in the list: a candidate of the mail, or the customer the Vorgang has now. */
type Choice = CandidateOut & { current: boolean };

interface CustomerFromMailProps {
  caseId: string;
  /** The mail's entry in the Vorgang's record (`?mail=` on the Eingang). */
  entryId: string;
  /**
   * The customer is the Vorgang's now, with the contact person — the block
   * shows the card itself; the owner moves on (the Eingang to its next step,
   * a dialog closes). `null` is reserved for an owner that lets the person
   * leave without one; the block itself never assigns nobody.
   */
  onDone: (customer: CustomerOut | null) => void;
  /** 2 on a screen; 3 inside a dialog that has its own title. */
  headingLevel?: 2 | 3;
  /**
   * A request is out, or not any more — for an owner that must not let the
   * person leave meanwhile (a dialog holds Escape, as the record's customer
   * field does, record fix round 2, I-2). Not called once the block is gone.
   */
  onBusy?: (busy: boolean) => void;
  /**
   * Where the contact fields start. `"case"` (the Eingang): the Vorgang's
   * stored contact, else the mail's — the mail is the Vorgang's own, and a
   * contact corrected by hand stays. `"mail"` (the record's „Kunde aus dieser
   * Mail“, final review M-2): this mail's clues, wherever it could be read —
   * a later mail may be written by somebody else. A stored contact that
   * differs is said to be replaced before anything is saved.
   */
  contactFrom?: "case" | "mail";
}

/**
 * „Wer ist der Kunde?" from a filed mail (spec 2026-10-20 §3.1, §3.3) — the
 * Eingang's right column and the record's „Kunde aus dieser Mail".
 *
 * The rules read the mail (D-6); the block shows what they read and where it
 * stands, in words — never a percentage —, and the customers of the master it
 * might be, each with its reasons. **Nothing is assigned without a press**
 * (D-2, A-014): no candidate is preselected, and „Das ist der Kunde" waits
 * for a choice. Three ways, always: take a candidate, create a new customer
 * from what was read (also when a candidate fits — another firm of the same
 * person, D-2), or search the master. Nothing to choose from: the form is
 * open. An unreadable mail says so and opens the form empty (D-3).
 *
 * The contact person (D-8) is saved with the customer — first, so a refused
 * assignment leaves nothing visible behind and the next press writes both
 * again. It is edited on its own only when it is somebody else than the
 * customer (`otherPerson`, the card's rule): for a private customer who wrote
 * herself it is one line, with the way to name another person.
 *
 * With a customer already assigned (a reload, the record) the block is the
 * card and „Ändern". The state comes from the server: `useCase` for the
 * customer and the contact, the proposal for the rest.
 */
export function CustomerFromMail({
  caseId,
  entryId,
  onDone,
  headingLevel = 2,
  onBusy,
  contactFrom = "case",
}: CustomerFromMailProps) {
  const Heading = headingLevel === 3 ? "h3" : "h2";
  const id = useId();
  const client = useQueryClient();
  const detail = useCase(caseId);
  const proposal = useCustomerProposal(caseId, entryId);
  const [mode, setMode] = useState<Mode | null>(null);
  // Assigned by this block, before the Vorgang is read again.
  const [assigned, setAssigned] = useState<{ customer: CustomerOut; contact: ContactOut | null } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [contact, setContact] = useState<ContactDraft | null>(null);
  const [otherContact, setOtherContact] = useState(false);
  const [contactEmailError, setContactEmailError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // What a new customer starts from: the mail's fields, or a search's text over them.
  const [createFrom, setCreateFrom] = useState<{ key: number; initial: Partial<CustomerDraft> } | null>(null);
  const [busy, setBusy] = useState(false);
  // Why the last press did not assign: what failed, and the server's sentence about it.
  const [refused, setRefused] = useState<{ title: string; text: string | null } | null>(null);
  const [gone, setGone] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const blockRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const contactNameRef = useRef<HTMLInputElement>(null);
  const contactEmailRef = useRef<HTMLInputElement>(null);
  // The control that takes the focus once the next render stands — the one
  // that was pressed is often gone with the mode it belonged to, and a field
  // that is asked for may only be drawn by that render. The tick makes every
  // request a change, also one for the same control twice.
  const focusAfter = useRef<string | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  // The assignment the owner has not been told of yet. Told after the commit
  // that shows the card (Task 10 review, I-1): told inside the press, the
  // owner put the focus on its next step — and the form, still mounted for a
  // moment, took it back for its own button, which the next render removed.
  const [done, setDone] = useState<{ customer: CustomerOut } | null>(null);
  const onDoneRef = useRef(onDone);
  const mounted = useRef(true);

  useEffect(() => {
    onDoneRef.current = onDone;
  });

  useEffect(() => {
    if (done !== null) onDoneRef.current(done.customer);
  }, [done]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const target = focusAfter.current;
    if (target === null) return;
    focusAfter.current = null;
    const named: Record<string, HTMLElement | null> = {
      search: searchRef.current,
      "contact-name": contactNameRef.current,
      "contact-email": contactEmailRef.current,
    };
    (named[target] ?? blockRef.current?.querySelector<HTMLElement>(`[data-focus="${target}"]`))?.focus();
  }, [focusTick]);

  function focusNext(target: string | null) {
    focusAfter.current = target;
    if (target !== null) setFocusTick((tick) => tick + 1);
  }

  const data: CustomerProposalOut | undefined = proposal.data;
  const readable = data?.readable === true;
  const fields = readable ? data.fields : null;
  const candidates = readable ? data.candidates : [];
  const stored = detail.data?.customer ?? null;
  const current = assigned?.customer ?? stored;
  const shownMode: Mode = mode ?? (current !== null ? "card" : candidates.length > 0 || proposal.isError ? "choose" : "create");

  if (detail.isPending || proposal.isPending) {
    return (
      <div className={styles.block} role="region" aria-label="Kunde wird gelesen" aria-busy="true">
        <Bone width="4rem" height="0.75rem" />
        <Bone width="70%" />
        <Bone width="55%" />
        <Bone width="85%" height="4.5rem" />
      </div>
    );
  }

  const storedContact = detail.data?.contact ?? null;
  const storedDraft = storedContact !== null ? draftOf(storedContact) : null;
  // This mail's contact where it was asked for and could be read; an
  // unreadable mail names nobody, and would only clear who is stored.
  const fromThisMail = contactFrom === "mail" && readable;
  const contactDraft: ContactDraft =
    contact ?? (storedDraft !== null && !fromThisMail ? storedDraft : contactFromMail(data));
  const replacesStored =
    fromThisMail && storedDraft !== null && hasContact(storedDraft) && !sameContact(storedDraft, contactDraft);

  // The candidates — and the customer the Vorgang has now, where the mail
  // does not point at them: „Ändern" starts from who is there.
  const choices: Choice[] = [
    ...(current !== null && !candidates.some((candidate) => candidate.customer.id === current.id)
      ? [{ customer: current, reasons: [], current: true }]
      : []),
    ...candidates.map((candidate) => ({ ...candidate, current: candidate.customer.id === current?.id })),
  ];
  const chosen = choices.find((choice) => choice.customer.id === selected)?.customer ?? null;
  // Who the customer would be, for the „dieselbe Person" rule: the chosen
  // one, else what the mail says about them.
  const target =
    shownMode === "choose" && chosen !== null
      ? chosen
      : { company: fields?.company?.value ?? null, displayName: fields?.name?.value ?? "" };
  const samePerson = contactDraft.name.trim() !== "" && !otherPerson(target, { name: contactDraft.name });
  const contactOpen = !samePerson || otherContact;

  function changeContact(key: keyof ContactDraft, value: string) {
    if (key === "email") setContactEmailError(null);
    setContact({ ...contactDraft, [key]: value });
  }

  /** The contact's address the way the server takes it — said at its field, before anything is sent. */
  function contactReady(): boolean {
    const email = contactDraft.email.trim();
    if (email === "" || email.includes("@")) return true;
    setContactEmailError(EMAIL_WITHOUT_AT);
    // A collapsed contact opens: the address is wrong, it has to be seen.
    setOtherContact(true);
    focusNext("contact-email");
    return false;
  }

  /** Contact first, then the customer: a refused assignment leaves nothing on screen that is not so. */
  async function save(customer: CustomerOut): Promise<void> {
    const saved = {
      contactName: blankToNull(contactDraft.name),
      contactEmail: blankToNull(contactDraft.email),
      contactPhone: blankToNull(contactDraft.phone),
    };
    await setCaseContact(caseId, saved);
    await assignCustomer(caseId, customer.id);
    // The Vorgang (the card after a reload, the record, the frame) and every
    // list that names its customer.
    void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
    void client.invalidateQueries({ queryKey: CASES_KEY });
    if (!mounted.current) return;
    setAssigned({
      customer,
      contact:
        saved.contactName === null && saved.contactEmail === null && saved.contactPhone === null
          ? null
          : { name: saved.contactName, email: saved.contactEmail, phone: saved.contactPhone },
    });
    setMode("card");
    setRefused(null);
    setAnnouncement(`„${customer.company ?? customer.displayName}“ ist jetzt der Kunde dieses Vorgangs.`);
    // A new object every time: the same customer assigned again is told again.
    setDone({ customer });
  }

  /** The press is out, or answered: said to the block and to its owner. */
  function hold(on: boolean) {
    if (!mounted.current) return;
    setBusy(on);
    onBusy?.(on);
  }

  function refusedBy(cause: unknown) {
    if (caseGone(cause)) {
      setGone(true);
      setRefused({ title: CASE_GONE_SENTENCE, text: null });
      void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      return;
    }
    setRefused({ title: "Kunde nicht zugeordnet.", text: messageOf(cause) });
  }

  /** „Das ist der Kunde", or a customer picked in the search. */
  async function confirm(customer: CustomerOut | null) {
    if (customer === null || busy || gone || !contactReady()) return;
    hold(true);
    setRefused(null);
    try {
      await save(customer);
    } catch (cause) {
      refusedBy(cause);
    } finally {
      hold(false);
    }
  }

  /**
   * The form created (or took) a customer: it shows a refusal itself, so it
   * is thrown back. The contact's address was checked before the form wrote
   * anything (`guard`).
   */
  async function created(customer: CustomerOut): Promise<void> {
    try {
      await save(customer);
    } catch (cause) {
      // Gone: said once for the block, and the form is held (`disabled`).
      if (caseGone(cause)) refusedBy(cause);
      throw cause;
    }
  }

  function go(next: Mode, focus: string | null) {
    focusNext(focus);
    setRefused(null);
    setMode(next);
  }

  /** Back from the form or the search to the choice — the focus on the button that left it. */
  function back(from: "create" | "search") {
    go("choose", from);
  }

  function openCreate(initial: Partial<CustomerDraft>) {
    setCreateFrom((previous) => ({ key: (previous?.key ?? 0) + 1, initial }));
    go("create", null);
  }

  const mailDraft = fields !== null ? draftFromMail(fields) : {};
  const rows = fields !== null ? readRows(fields, detail.data?.mailRequest ?? null) : [];
  // A grating type or a coating alone tells nobody who the customer is.
  const recognisable = rows.some((row) => !ASKED_FOR.has(row.label));
  const trouble = proposal.error ? proposalTrouble(proposal.error) : null;
  const hintId = `${id}-choose-hint`;
  const refusedId = `${id}-refused`;
  const held = busy || gone ? "true" : undefined;

  const readSummary =
    readable && (shownMode === "choose" || shownMode === "search") ? (
      rows.length > 0 ? (
        <div className={styles.read}>
          <p className="label">Aus der Mail gelesen</p>
          <dl className={styles.readList}>
            {rows.map((row) => (
              <div key={row.label} className={styles.readRow}>
                <dt>{row.label}</dt>
                <dd>
                  <span className={styles.readValue}>{row.value}</span>{" "}
                  <span className={styles.from}>· {row.from}</span>
                </dd>
              </div>
            ))}
          </dl>
          {data.references.length > 0 ? (
            <p className={styles.references}>
              Bezug: <span className="tabular">{data.references.join(", ")}</span>
            </p>
          ) : null}
          {recognisable ? null : <p className={styles.quiet}>{NOTHING_RECOGNISABLE}</p>}
        </div>
      ) : (
        <p className={styles.quiet}>{NOTHING_RECOGNISABLE}</p>
      )
    ) : null;

  return (
    <div ref={blockRef} className={styles.block}>
      <section className={styles.section} aria-labelledby={`${id}-customer`}>
        <div className={styles.head}>
          <Heading id={`${id}-customer`} className={styles.title}>
            {/* At level 2 it is the Eingang panel's title and carries the mesh
                marker as `Panel` does (design 2026-10-24 §8.6); in the
                record's dialog (level 3) it is a section of the dialog. */}
            {headingLevel === 2 ? <span className="marke" aria-hidden="true" /> : null}
            Kunde
          </Heading>
          {shownMode === "card" && current !== null ? (
            <Button
              data-focus="change"
              aria-disabled={held}
              onClick={() => {
                setSelected(current.id);
                setAnnouncement("");
                go("choose", "choice");
              }}
            >
              Ändern
            </Button>
          ) : null}
        </div>

        {!readable && data !== undefined ? <StateMessage strong live={false} title={UNREADABLE} /> : null}
        {trouble !== null ? (
          <StateMessage
            tone="neutral"
            strong
            live={false}
            title={trouble.text}
            action={
              trouble.retry ? (
                <Button busy={proposal.isFetching} onClick={() => void proposal.refetch()}>
                  {proposal.isFetching ? "Wird gelesen …" : "Erneut versuchen"}
                </Button>
              ) : undefined
            }
          />
        ) : null}

        {shownMode === "card" && current !== null ? (
          <CustomerCard customer={current} contact={assigned?.contact ?? storedContact} />
        ) : null}

        {readSummary}

        {shownMode === "choose" ? (
          <>
            {choices.length > 0 ? (
              <fieldset className={styles.choices} aria-describedby={hintId}>
                <legend className="label">Im Kundenstamm</legend>
                <p id={hintId} className={styles.hint}>
                  Wähl aus, wer der Kunde ist — zugeordnet wird erst mit „Das ist der Kunde“.
                </p>
                <div className={styles.choiceList}>
                  {choices.map((choice) => (
                    <ChoiceRow
                      key={choice.customer.id}
                      name={`${id}-choice`}
                      choice={choice}
                      checked={selected === choice.customer.id}
                      focusMark={selected === choice.customer.id || (selected === null && choice === choices[0]) ? "choice" : undefined}
                      onSelect={() => {
                        setSelected(choice.customer.id);
                        setRefused(null);
                      }}
                    />
                  ))}
                </div>
              </fieldset>
            ) : trouble === null && readable ? (
              <p className={styles.quiet}>Kein Treffer im Kundenstamm.</p>
            ) : null}
            <div className={styles.actions}>
              {choices.length > 0 ? (
                <Button
                  variant="primary"
                  busy={busy}
                  aria-disabled={selected === null || gone ? "true" : undefined}
                  aria-describedby={[selected === null ? hintId : null, refused !== null ? refusedId : null].filter(Boolean).join(" ") || undefined}
                  onClick={() => void confirm(chosen)}
                >
                  {busy ? "Wird zugeordnet …" : "Das ist der Kunde"}
                </Button>
              ) : null}
              <Button data-focus="create" aria-disabled={held} onClick={() => openCreate(mailDraft)}>
                Neuen Kunden anlegen
              </Button>
              <Button data-focus="search" aria-disabled={held} onClick={() => go("search", "search")}>
                Anderen Kunden suchen
              </Button>
              {current !== null ? (
                <Button variant="quiet" aria-disabled={busy ? "true" : undefined} onClick={() => go("card", "change")}>
                  Abbrechen
                </Button>
              ) : null}
            </div>
          </>
        ) : null}

        {shownMode === "search" ? (
          <div className={styles.search}>
            <CustomerSearch
              label="Kunden im Stamm suchen"
              query={query}
              onQueryChange={setQuery}
              onPick={(customer) => void confirm(customer)}
              onCreate={(text) =>
                openCreate({ ...mailDraft, ...(text.includes("@") ? { email: text } : { displayName: text }) })
              }
              onEscape={() => back("search")}
              busy={busy}
              held={gone}
              inputRef={searchRef}
            />
            <div className={styles.actions}>
              <Button aria-disabled={busy ? "true" : undefined} onClick={() => back("search")}>
                Abbrechen
              </Button>
            </div>
          </div>
        ) : null}

        {shownMode === "create" && choices.length === 0 ? (
          // The form is open because nothing fits — said, with the other way.
          <div className={styles.noMatch}>
            {readable ? <p className={styles.quiet}>Kein Treffer im Kundenstamm.</p> : null}
            <Button variant="quiet" flush data-focus="search" aria-disabled={held} onClick={() => go("search", "search")}>
              Im Kundenstamm suchen
            </Button>
          </div>
        ) : null}

        {shownMode === "create" ? (
          <CustomerForm
            key={createFrom?.key ?? 0}
            initial={createFrom?.initial ?? mailDraft}
            source={readable ? "mail" : "manual"}
            sources={fields !== null ? sourcesFromMail(fields) : undefined}
            guard={contactReady}
            onCreated={created}
            onCancel={() => back("create")}
            onBusy={(on) => {
              if (mounted.current) onBusy?.(on);
            }}
            disabled={gone}
            submitDescribedBy={gone ? refusedId : undefined}
            submitLabel="Anlegen und zuordnen"
            cancelLabel={choices.length > 0 || current !== null ? "Zurück zur Auswahl" : "Abbrechen"}
          />
        ) : null}

        {refused !== null ? (
          <StateMessage id={refusedId} tone="conflict" live="alert" title={refused.title}>
            {refused.text}
          </StateMessage>
        ) : null}
      </section>

      {shownMode !== "card" ? (
        <section className={styles.section} aria-labelledby={`${id}-contact`}>
          <div className={styles.head}>
            <Heading id={`${id}-contact`} className={styles.title}>
              Ansprechpartner
            </Heading>
          </div>
          <p className={styles.hint}>Wer die Anfrage geschrieben hat — wird mit dem Kunden gespeichert.</p>
          {replacesStored ? <p className={styles.replaces}>Der bisherige Ansprechpartner wird ersetzt.</p> : null}
          {contactOpen ? (
            // A group named by the heading: „E-Mail" and „Telefon" stand in
            // the customer form too, and are read with whose they are.
            <div className={styles.contactFields} role="group" aria-labelledby={`${id}-contact`}>
              <TextField
                id={`${id}-contact-name`}
                label="Name"
                value={contactDraft.name}
                inputRef={contactNameRef}
                onChange={(value) => changeContact("name", value)}
              />
              <TextField
                id={`${id}-contact-email`}
                label="E-Mail"
                type="email"
                value={contactDraft.email}
                error={contactEmailError}
                inputRef={contactEmailRef}
                onChange={(value) => changeContact("email", value)}
              />
              <TextField
                id={`${id}-contact-phone`}
                label="Telefon"
                type="tel"
                value={contactDraft.phone}
                onChange={(value) => changeContact("phone", value)}
              />
            </div>
          ) : (
            <div className={styles.same}>
              <p className={styles.sameText}>Dieselbe Person wie der Kunde.</p>
              <Button
                variant="quiet"
                flush
                onClick={() => {
                  setOtherContact(true);
                  focusNext("contact-name");
                }}
              >
                Andere Person angeben
              </Button>
            </div>
          )}
        </section>
      ) : null}

      {/* One region that was there before its words (F-13): a status mounted
          with its own text is not reliably read. */}
      <p role="status" className="visually-hidden">
        {announcement}
      </p>
    </div>
  );
}

interface ChoiceRowProps {
  name: string;
  choice: Choice;
  checked: boolean;
  /** The row the focus goes to when the list opens from the card. */
  focusMark: string | undefined;
  onSelect: () => void;
}

/**
 * One customer of the master as a radio: who (the firm leads, the person
 * under it), where and with which number, and why the rules think so — the
 * reasons as the server words them. „in JTL gesperrt" with the card's lock.
 */
function ChoiceRow({ name, choice, checked, focusMark, onSelect }: ChoiceRowProps) {
  const id = useId();
  const { customer } = choice;
  const meta = [
    customer.company !== null ? customer.displayName : null,
    placeOf(customer),
    customer.customerNo !== null ? `Kd.-Nr. ${customer.customerNo}` : null,
  ].filter((part): part is string => part !== null && part !== "");
  const reasons = [...(choice.current ? ["bisher zugeordnet"] : []), ...choice.reasons.map((reason) => reason.label)];
  return (
    <label className={styles.choice} data-checked={checked ? "" : undefined}>
      <input
        type="radio"
        className={styles.radio}
        name={name}
        value={customer.id}
        checked={checked}
        data-focus={focusMark}
        aria-labelledby={`${id}-name`}
        aria-describedby={`${id}-meta ${id}-why`}
        onChange={onSelect}
      />
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.choiceText}>
        <span id={`${id}-name`} className={styles.choiceName}>
          {customer.company ?? customer.displayName}
        </span>
        <span id={`${id}-meta`} className={styles.choiceMeta}>
          {meta.join(" · ")}
          {customer.jtlBlocked ? (
            <>
              {" "}
              <span className={styles.blocked}>
                <LockIcon size={12} />
                in JTL gesperrt
              </span>
            </>
          ) : null}
          {/* Read before the reasons, which follow in the same description:
              a full stop between them, for the ear only. */}
          {meta.length > 0 || customer.jtlBlocked ? <span className="visually-hidden">.</span> : null}
        </span>
        <span id={`${id}-why`} className={styles.why}>
          {reasons.join(" · ")}
        </span>
      </span>
    </label>
  );
}
